"use client"
import { useNavigate } from "react-router-dom"
import DashboardTemplate from "../templates/DashboardTemplate"
import useFetch from "../../hooks/useFetch"
import Card from "../molecules/Card"
import Button from "../atoms/Button"
import { useAuth } from "../../hooks/useAuth"
import { Clock, CheckCircle, XCircle, Truck, Coffee, MessageSquare, MapPin, Calendar, Package } from "lucide-react"

const Spinner = () => (
  <div className="flex justify-center items-center py-12">
    <svg
      className="animate-spin h-10 w-10 text-amber-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  </div>
)

const StatusBadge = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case "Pending":
        return { icon: Clock, color: "bg-amber-100 text-amber-800 border-amber-200" }
      case "Accepted":
        return { icon: CheckCircle, color: "bg-blue-100 text-blue-800 border-blue-200" }
      case "In Progress":
        return { icon: Coffee, color: "bg-purple-100 text-purple-800 border-purple-200" }
      case "Ready":
        return { icon: ClipboardList, color: "bg-green-100 text-green-800 border-green-200" }
      case "Completed":
        return { icon: CheckCircle, color: "bg-green-100 text-green-800 border-green-200" }
      case "Cancelled":
        return { icon: XCircle, color: "bg-red-100 text-red-800 border-red-200" }
      default:
        return { icon: Clock, color: "bg-gray-100 text-gray-800 border-gray-200" }
    }
  }

  const { icon: Icon, color } = getStatusConfig()

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      <Icon className="w-3.5 h-3.5 mr-1" />
      {status}
    </span>
  )
}

const ClipboardList = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
    <path d="M9 12h6" />
    <path d="M9 16h6" />
    <path d="M9 8h1" />
  </svg>
)

export default function OrderTrackingPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const userId = user?._id
  const { data: orders = [], loading, error } = useFetch(userId ? `/orders?user=${userId}` : null)

  if (loading)
    return (
      <DashboardTemplate>
        <div className="p-6">
          <h1 className="text-3xl font-bold mb-6 text-gray-800">Your Orders</h1>
          <Spinner />
        </div>
      </DashboardTemplate>
    )

  if (error)
    return (
      <DashboardTemplate>
        <div className="p-6">
          <h1 className="text-3xl font-bold mb-6 text-gray-800">Your Orders</h1>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>Error: {error.message}</p>
          </div>
        </div>
      </DashboardTemplate>
    )

  const sorted = [...orders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

  return (
    <DashboardTemplate>
      <div className="p-6">
        <div className="flex items-center mb-6">
          <Package className="h-7 w-7 mr-2 text-amber-600" />
          <h1 className="text-3xl font-bold text-gray-800">Your Orders</h1>
        </div>

        {sorted.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
            <Package className="mx-auto h-12 w-12 text-amber-500 mb-3" />
            <h3 className="text-lg font-medium text-gray-900">No orders yet</h3>
            <p className="mt-2 text-sm text-gray-500">When you place orders, they will appear here.</p>
            <Button
              onClick={() => navigate("/browse-restaurants")}
              className="mt-4 bg-amber-600 hover:bg-amber-700 text-white"
            >
              Browse Restaurants
            </Button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {sorted.map((order) => (
              <Card
                key={order._id}
                className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-lg">Order #{order._id.slice(-6).toUpperCase()}</h3>
                  <StatusBadge status={order.status} />
                </div>

                <div className="space-y-2 mb-4">
                  <p className="flex items-center text-gray-700">
                    <Coffee className="h-4 w-4 mr-2 text-amber-600" />
                    <span className="font-medium">Restaurant:</span>
                    <span className="ml-1">{order.restaurant.name}</span>
                  </p>

                  <p className="flex items-center text-gray-700">
                    {order.orderType === "delivery" ? (
                      <Truck className="h-4 w-4 mr-2 text-amber-600" />
                    ) : (
                      <Coffee className="h-4 w-4 mr-2 text-amber-600" />
                    )}
                    <span className="font-medium">Type:</span>
                    <span className="ml-1 capitalize">{order.orderType}</span>
                  </p>

                  {order.orderType === "dine-in" && order.tableNumber && (
                    <p className="flex items-center text-gray-700">
                      <MapPin className="h-4 w-4 mr-2 text-amber-600" />
                      <span className="font-medium">Table #:</span>
                      <span className="ml-1">{order.tableNumber}</span>
                    </p>
                  )}

                  {order.orderType === "delivery" && order.deliveryAddress && (
                    <p className="flex items-center text-gray-700">
                      <MapPin className="h-4 w-4 mr-2 text-amber-600" />
                      <span className="font-medium">Deliver to:</span>
                      <span className="ml-1 truncate">{order.deliveryAddress}</span>
                    </p>
                  )}

                  <p className="flex items-center text-gray-700">
                    <Calendar className="h-4 w-4 mr-2 text-amber-600" />
                    <span className="font-medium">Date:</span>
                    <span className="ml-1">{new Date(order.createdAt).toLocaleDateString()}</span>
                  </p>
                </div>

                <div className="border-t border-gray-100 pt-3 mb-3">
                  <h4 className="font-medium text-gray-800 mb-2">Order Items:</h4>
                  <ul className="space-y-1">
                    {order.orderItems.map((oi, i) => (
                      <li key={i} className="text-sm flex justify-between">
                        <span>{oi.item.name}</span>
                        <span className="text-gray-600">× {oi.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex space-x-2 mt-4">
                  {order.orderType === "delivery" && (
                    <Button
                      onClick={() => navigate(`/track/${order._id}`)}
                      className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <Truck className="h-4 w-4 mr-1" />
                      Track
                    </Button>
                  )}
                  <Button
                    onClick={() => navigate(`/chat/${order._id}`)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Chat
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardTemplate>
  )
}
